import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye, EyeSlash, Sparkle } from "@phosphor-icons/react";
import { authService } from "../services/authService";
import Logo from "./Logo";

export default function LoginForm({ onRequestAccess }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);

  const loginMutation = useMutation({
    mutationFn: ({ email: em, password: pw }) => authService.login({ email: em, password: pw }),
    onSuccess: () => {
      toast.success("Bienvenido de vuelta.");
      navigate("/map", { replace: true });
    },
    onError: (err) => {
      toast.error(err?.message || "No se pudo iniciar sesión");
    },
  });

  function onSubmit(e) {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email y contraseña son requeridos");
      return;
    }
    loginMutation.mutate({ email, password });
  }

  return (
    <div className="zx-card zx-card-static zx-glass h-full space-y-10 p-7 md:p-8" data-testid="login-form">
      <header className="flex flex-col items-center gap-6 text-center">
        <Logo size={88} />
        <p className="text-sm text-[rgba(255,255,255,0.65)]">
          Tu identidad permanece oculta. Solo tu deseo entra contigo.
        </p>
      </header>

      <form onSubmit={onSubmit} className="space-y-6">
        <div className="space-y-1">
          <label className="zx-label" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className="zx-input"
            placeholder="tu@correo.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input"
          />
        </div>

        <div className="space-y-1">
          <label className="zx-label" htmlFor="password">
            Contraseña
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPwd ? "text" : "password"}
              autoComplete="current-password"
              className="zx-input pr-10"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPwd((s) => !s)}
              aria-label={showPwd ? "Ocultar contraseña" : "Mostrar contraseña"}
              className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-[rgba(255,255,255,0.45)] hover:text-[#f5f5f5]"
              data-testid="login-toggle-password"
            >
              {showPwd ? <EyeSlash size={18} weight="thin" /> : <Eye size={18} weight="thin" />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          className="zx-btn-primary flex items-center justify-center gap-2"
          disabled={loginMutation.isPending}
          data-testid="login-submit-button"
        >
          {loginMutation.isPending ? "Entrando..." : "Entrar"}
          <Sparkle size={14} weight="thin" />
        </button>
      </form>

      <div className="flex flex-col items-center gap-3 text-center">
        <Link
          to="/forgot-password"
          className="text-xs uppercase tracking-[0.2em] text-[rgba(255,255,255,0.55)] hover:text-[#f5f5f5]"
          data-testid="login-forgot-link"
        >
          ¿Olvidaste tu contraseña?
        </Link>
        <div className="zx-divider" />
        <p className="text-sm text-[rgba(255,255,255,0.65)]">
          ¿Vas a quedarte con la duda?{" "}
          <button
            type="button"
            className="text-[#e9b5c5] underline decoration-[rgba(184,50,90,0.5)] underline-offset-4 hover:text-[#f5f5f5]"
            onClick={onRequestAccess}
            data-testid="login-register-toggle"
            aria-label="Voltear tarjeta al formulario de solicitud de acceso"
          >
            Pide acceso
          </button>
        </p>
      </div>
    </div>
  );
}
